<template>
  <div class="toolbox-container" :class="{'--is-right-panel-active': rightNavActiveTab}">
    <div class="toolbox-cards">
      <!-- Choose a slide -->
      <button
        class="toolbox-card"
        @click="chooseASlide"
        @keydown.enter="chooseASlide"
        @mouseenter="startAnimation('chooseSlide')"
        @mouseleave="stopAnimation('chooseSlide')"
        @focus="startAnimation('chooseSlide')"
        @blur="stopAnimation('chooseSlide')"
        name="blank-presentation-create-blank-slide-button"
        aria-label="Choose a slide - Quiz, Word Cloud, Poll and more"
        v-aha-emit-action
      >
        <div class="card-icon" aria-hidden="true">
          <aha-lottie-player
            :animationData="require('@/assets/animations/blank-presentation/choose-slide.json')"
            :auto-play="false"
            :loop="true"
            :play-state="playStates.chooseSlide"
            external-control
            @loopComplete="onLoopComplete('chooseSlide')"
          />
        </div>
        <h3 class="card-title">
          {{ $i18nPresenter.t('Choose a slide') }}
        </h3>
        <p class="card-description">
          {{ $i18nPresenter.t('Quiz, Word Cloud, Poll and more') }}
        </p>
      </button>

      <!-- Import -->
      <button
        class="toolbox-card"
        @click="importPresentation"
        @keydown.enter="importPresentation"
        @mouseenter="startAnimation('import')"
        @mouseleave="stopAnimation('import')"
        @focus="startAnimation('import')"
        @blur="stopAnimation('import')"
        name="blank-presentation-import-button"
        aria-label="Import - PDF, PPT or PPTX"
        :class="{'disabled': isCurrentPresentationImportingSlides}"
        :disabled="isCurrentPresentationImportingSlides"
        v-aha-emit-action
      >
        <div class="card-icon" aria-hidden="true">
          <aha-lottie-player
            :animationData="require('@/assets/animations/blank-presentation/import.json')"
            :auto-play="false"
            :loop="true"
            :play-state="playStates.import"
            external-control
            @loopComplete="onLoopComplete('import')"
          />
        </div>
        <h3 class="card-title">
          {{ $i18nPresenter.t('Import') }}
        </h3>
        <!-- Add pb-3 to align Import title with other cards that have multi-line descriptions -->
        <p class="card-description pb-3">
          {{ $i18nPresenter.t('PDF, PPT or PPTX') }}
        </p>
      </button>

      <!-- Create with AI -->
      <button
        class="toolbox-card"
        @click="createWithAI"
        @keydown.enter="createWithAI"
        @mouseenter="startAnimation('ai')"
        @mouseleave="stopAnimation('ai')"
        @focus="startAnimation('ai')"
        @blur="stopAnimation('ai')"
        name="blank-presentation-create-with-ai-button"
        aria-label="Create with AI - Got an idea? Let our trained AI handle the rest"
        v-aha-emit-action
      >
        <div class="card-icon" aria-hidden="true">
          <aha-lottie-player
            :animationData="require('@/assets/animations/blank-presentation/AI-sparkle.json')"
            :auto-play="false"
            :loop="true"
            :play-state="playStates.ai"
            external-control
            @loopComplete="onLoopComplete('ai')"
          />
        </div>
        <h3 class="card-title">
          {{ $i18nPresenter.t('Create with AI') }}
        </h3>
        <p class="card-description">
          {{ $i18nPresenter.t('Got an idea? Let our trained AI handle the rest.') }}
        </p>
      </button>

      <!-- Use template -->
      <button
        class="toolbox-card"
        @click="useTemplate"
        @keydown.enter="useTemplate"
        @mouseenter="startAnimation('useTemplate')"
        @mouseleave="stopAnimation('useTemplate')"
        @focus="startAnimation('useTemplate')"
        @blur="stopAnimation('useTemplate')"
        name="blank-presentation-use-template-button"
        aria-label="Use a template - Thousands of pro templates for any event"
        v-aha-emit-action
      >
        <div class="card-icon" aria-hidden="true">
          <aha-lottie-player
            :animationData="require('@/assets/animations/blank-presentation/template.json')"
            :auto-play="false"
            :loop="true"
            :play-state="playStates.useTemplate"
            external-control
            @loopComplete="onLoopComplete('useTemplate')"
          />
        </div>
        <h3 class="card-title">
          {{ $i18nPresenter.t('Use a template') }}
        </h3>
        <p class="card-description">
          {{ $i18nPresenter.t('Thousands of pro templates for any event') }}
        </p>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AhaLottiePlayer from '@mindthegapstudio/stpancras-storybook-app/src/components/UI/lottie-player/LottiePlayer'

const LOOP_DELAY_MS = 1000

export default {
  name: 'BlankPresentation',

  components: {
    AhaLottiePlayer,
  },

  data() {
    return {
      playStates: {
        ai: 'stop',
        chooseSlide: 'stop',
        import: 'stop',
        useTemplate: 'stop',
      },
      loopTimeouts: {},
    }
  },
  computed: {
    ...mapGetters('rightNav', ['rightNavTabNames', 'rightNavActiveTab']),
    ...mapGetters('aiChatbotImportPptxPdf', ['isCurrentPresentationImportingSlides']),

  },
  beforeDestroy() {
    Object.values(this.loopTimeouts).forEach((timeout) => {
      if (timeout) clearTimeout(timeout)
    })
  },
  methods: {
    ...mapActions(['setShowModalTemplateLibraryAction', 'setSlideSearchPopupVisibilityAction']),
    ...mapActions('rightNav', ['setRightNavActiveTabAction']),
    ...mapActions('aiChatbotImportPptxPdf', ['setIsAIChatbotImportModalVisibleV2Action']),

    onLoopComplete(key) {
      this.playStates[key] = 'pause'
      // Clear existing timeout if any to prevent memory leak
      if (this.loopTimeouts[key]) {
        clearTimeout(this.loopTimeouts[key])
      }
      this.loopTimeouts[key] = setTimeout(() => {
        this.playStates[key] = 'play'
      }, LOOP_DELAY_MS)
    },

    startAnimation(key) {
      this.playStates[key] = 'play'
    },

    stopAnimation(key) {
      if (this.loopTimeouts[key]) {
        clearTimeout(this.loopTimeouts[key])
      }
      this.playStates[key] = 'stop'
    },

    chooseASlide() {
      this.setSlideSearchPopupVisibilityAction(true)
    },
    importPresentation() {
      this.setIsAIChatbotImportModalVisibleV2Action(true)
    },

    onAIButtonClicked() {
      this.setRightNavActiveTabAction(this.rightNavTabNames.AI)
    },
    createWithAI() {
      this.onAIButtonClicked()
    },
    useTemplate() {
      this.setShowModalTemplateLibraryAction(true)
    },
  },
}
</script>
<style scoped lang="scss">
// Breakpoints
$breakpoint-max-width-tablet: 1280px;
$breakpoint-min-width-desktop: 1281px;
$breakpoint-max-width-desktop: 1920px;
$breakpoint-max-height-compact: 520px;

// Card dimensions
$card-width: 204px;
$card-height: 215px;
$card-border-radius: 12px;
$card-gap: 16px;

// Layout
$cards-min-height: 200px;
$cards-vertical-position: 40%;

// Spacing
$card-icon-spacing: 16px;
$card-title-spacing: 32px;
$card-title-font-size: 16px;

// Colors
$card-border-color: #D4D4D4;
$card-hover-color: #A96FF0;
$card-focus-shadow-width: 2px;
$card-title-color: #1A1A1A;

// Transitions
$card-transition-duration: 0.3s;

// Border
$card-border-width: 1px;

@mixin two-column-cards-layout {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  top: unset;
  transform: unset;
}

.toolbox-container {
  display: flex;
  width: 100%;
  justify-content: center;
  overflow-y: auto;
}

.toolbox-cards {
  @apply px-4;
  display: flex;
  gap: $card-gap;
  min-height: $cards-min-height;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
  max-height: max-content;
  top: $cards-vertical-position;
  transform: translate(0, -50%);
}

.toolbox-card {
  @apply pt-12 pb-7 px-6;
  background: white;
  border-radius: $card-border-radius;
  text-align: center;
  cursor: pointer;
  position: relative;
  width: $card-width;
  height: $card-height;
  transition: all $card-transition-duration ease;
  border: $card-border-width solid $card-border-color;
  outline: none;
  &.disabled {
    @apply cursor-not-allowed pointer-events-none;
  }
}

.toolbox-card:hover {
    border: $card-border-width solid $card-hover-color;
}
.toolbox-card:focus{
    box-shadow: 0 0 0 $card-focus-shadow-width $card-hover-color;
}

.card-icon {
    margin-bottom: $card-icon-spacing;
    display: flex;
    justify-content: center;
}

.card-title {
    font-size: $card-title-font-size;
    font-weight: 600;
    color: $card-title-color;
    margin-top: $card-title-spacing;
}

.card-description {
  @apply text-gray-70 mt-2 text-xs;
}

/* Responsive design */
@media (max-width: $breakpoint-max-width-tablet) {
  .toolbox-container {
    align-items: center;
  }
  .toolbox-cards {
    @include two-column-cards-layout;
  }
}
@media (min-width: $breakpoint-min-width-desktop) and (max-width: $breakpoint-max-width-desktop) {
  .toolbox-container.--is-right-panel-active {
    align-items: center;
    .toolbox-cards {
      @include two-column-cards-layout;
    }
  }
}
@media (max-height: $breakpoint-max-height-compact) {
  .toolbox-cards {
    height: 100%;
  }
}
</style>
